import StepsHeroSection from "../components/landing/StepsHeroSection"
import FilingChecklistSection from "../components/landing/FilingChecklistSection"
import FeeScheduleSection from "../components/landing/FeeScheduleSection"
import LandingTextSection from "../components/landing/LandingTextSection"
import InstantFilingSection from "../components/landing/InstantFilingSection"
import Footer from "../components/ui/Footer"
import { useSiteContent } from "../hooks/useSiteContent"

const DEFAULT_STEPS = [
  {
    title: "Application information",
    text: "Provide the applicant's name and postal address.",
  },
  {
    title: "Trademark",
    text: "Provide the name, wording and/or representation of your word or figurative trademark.",
  },
  {
    title: "Goods & services",
    text: "Provide your list of goods and services in English.",
  },
  {
    title: "Additional requests",
    text: "Provide any additional requests and, where applicable, priority information and earlier application details.",
  },
]

const DEFAULT_CHECKLIST_ITEMS = [
  "Applicant information: name and postal address",
  "Trademark: name and/or representation of the word or figurative mark",
  "Goods & services: list of goods and services in English",
  "Additional requests: priority, earlier application number, country and filing date, where applicable",
]

const DEFAULT_SERVICE_CARDS = [
  {
    title: "Trademark",
    image: "/images/trademark.svg",
    text: "Protect your brand throughout the European Union.",
    path: "/trademark",
  },
  {
    title: "Design",
    image: "/images/design.svg",
    text: "Protect the appearance of your products throughout the European Union.",
    path: "/design",
  },
  {
    title: "Patent",
    image: "/images/patent.svg",
    text: "Protect your technical inventions through the European patent system.",
    path: "/patent",
  },
]

export default function TrademarkPage() {
  const site = useSiteContent("trademark")

  const steps = DEFAULT_STEPS.map((step, index) => ({
    title: site.get(`trademark.hero.step.${index}.title`, step.title),
    text: site.get(`trademark.hero.step.${index}.text`, step.text),
  }))

  const checklistItems = DEFAULT_CHECKLIST_ITEMS.map((item, index) =>
    site.get(`trademark.checklist.item.${index}`, item)
  )

  const serviceCards = DEFAULT_SERVICE_CARDS.map((card, index) => ({
    ...card,
    title: site.get(`trademark.services.card.${index}.title`, card.title),
    text: site.get(`trademark.services.card.${index}.text`, card.text),
  }))

  return (
    <div className="flex min-h-screen flex-col">
      {site.isAdmin && (
        <button
          type="button"
          onClick={() => site.setEditMode(!site.editMode)}
          className={`fixed bottom-6 right-6 z-50 rounded-full border-2 border-white px-5 py-3 text-sm font-semibold shadow-xl transition ${
            site.editMode ? "bg-orange-500 text-white" : "bg-dantzBlue text-white"
          }`}
        >
          {site.editMode ? "Done editing" : "Edit page"}
        </button>
      )}

      <StepsHeroSection
        heading={site.get("trademark.hero.heading", "EU TRADEMARK")}
        description={site.get(
          "trademark.hero.description",
          "File your EU trademark online with professional representation before the European Union Intellectual Property Office (EUIPO). xpress-ip makes European trademark filing simple, fast and predictable."
        )}
        steps={steps}
        backgroundImage="/images/trademark.svg"
        editable={site.editMode}
        onSaveHeading={(value) => site.save("trademark.hero.heading", value)}
        onSaveDescription={(value) => site.save("trademark.hero.description", value)}
        onSaveStepTitle={(index, value) => site.save(`trademark.hero.step.${index}.title`, value)}
        onSaveStepText={(index, value) => site.save(`trademark.hero.step.${index}.text`, value)}
      />

      <FilingChecklistSection
        title={site.get("trademark.checklist.title", "FILING CHECKLIST")}
        items={checklistItems}
        editable={site.editMode}
        onSaveTitle={(value) => site.save("trademark.checklist.title", value)}
        onSaveItemText={(index, value) => site.save(`trademark.checklist.item.${index}`, value)}
      />

      <LandingTextSection
        heading={site.get("trademark.registration.heading", "REGISTRATION OFFICE")}
        variant="dark"
        editable={site.editMode}
        onSaveHeading={(value) => site.save("trademark.registration.heading", value)}
        body={[
          site.get(
            "trademark.registration.body.0",
            "Your EU trademark application is filed with the European Union Intellectual Property Office (EUIPO)."
          ),
        ]}
        onSaveBody={(index, value) => site.save(`trademark.registration.body.${index}`, value)}
      />

      <FeeScheduleSection
        title={site.get("trademark.fees.title", "CLEAR AND PREDICTABLE FEES")}
        description={site.get(
          "trademark.fees.description",
          "See the applicable service fees and official EUIPO fees before submitting your application."
        )}
        editable={site.editMode}
        onSaveTitle={(value) => site.save("trademark.fees.title", value)}
        onSaveDescription={(value) => site.save("trademark.fees.description", value)}
      />

      <LandingTextSection
        heading={site.get("trademark.notice.heading", "IMPORTANT NOTICE")}
        subheading={site.get("trademark.notice.subheading", "Before you file")}
        variant="dark"
        editable={site.editMode}
        onSaveHeading={(value) => site.save("trademark.notice.heading", value)}
        onSaveSubheading={(value) => site.save("trademark.notice.subheading", value)}
        body={[
          site.get(
            "trademark.notice.body.0",
            "Please make sure that all application information and documents you provide are complete and accurate."
          ),
          site.get(
            "trademark.notice.body.1",
            "xpress-ip handles the filing process and professional representation. Any substantive legal or strategic work beyond the filing service is only performed when requested or separately agreed."
          ),
        ]}
        onSaveBody={(index, value) => site.save(`trademark.notice.body.${index}`, value)}
      />

      <LandingTextSection
        heading={site.get("trademark.why.heading", "WHY XPRESS-IP?")}
        subheading={site.get(
          "trademark.why.subheading",
          "Your trademark. Our European filing expertise."
        )}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("trademark.why.heading", value)}
        onSaveSubheading={(value) => site.save("trademark.why.subheading", value)}
        body={[
          site.get(
            "trademark.why.body.0",
            "You provide the information. We prepare and submit the application and represent you before the EUIPO."
          ),
          site.get(
            "trademark.why.body.1",
            "There are no automatically performed cost-generating services after filing."
          ),
        ]}
        onSaveBody={(index, value) => site.save(`trademark.why.body.${index}`, value)}
      />

      <InstantFilingSection
        heading={site.get("trademark.services.heading", "INTELLECTUAL PROPERTY RIGHTS")}
        types={serviceCards}
        closingDivider={false}
        editable={site.editMode}
        onSaveHeading={(value) => site.save("trademark.services.heading", value)}
        onSaveCardTitle={(index, value) =>
          site.save(`trademark.services.card.${index}.title`, value)
        }
        onSaveCardText={(index, value) =>
          site.save(`trademark.services.card.${index}.text`, value)
        }
      />

      <Footer
        editable={site.editMode}
        email={site.get("trademark.footer.email", "info@xpress-ip.com")}
        tagline={site.get("trademark.footer.tagline", "EUROPEAN IP FILING, MADE SIMPLE.")}
        taglineSub={site.get("trademark.footer.taglineSub", "Patents · Trademarks · Designs")}
        onSaveEmail={(value) => site.save("trademark.footer.email", value)}
        onSaveTagline={(value) => site.save("trademark.footer.tagline", value)}
        onSaveTaglineSub={(value) => site.save("trademark.footer.taglineSub", value)}
      />
    </div>
  )
}
